import React, { useState } from 'react';
import { ChevronDown, ChevronRight, Globe, Plug, Code2, Trash2, Plus } from 'lucide-react';
import { NodeConfigFields } from 'features/agents/WorkflowEditor/NodeConfigFields';
import { Field, TextInput, cx, ghostBtnCls, removeBtnCls } from 'features/agents/WorkflowEditor/fields/inputs';

/**
 * The tools an AI Agent may call. Part of the agent, not nodes of their own.
 *
 * A plain list: one row per tool with its kind and name; a row opens to its
 * settings. MCP servers live here too — an MCP server is a source of tools,
 * not a step in the flow.
 *
 * value: [{ kind: 'http_request' | 'mcp' | 'code', name, description, config }]
 */
const TOOL_KINDS = {
  http_request: {
    label: 'HTTP request', icon: Globe,
    defaults: () => ({ method: 'GET', url: '', auth_type: 'none', headers: {}, query_params: {}, body_params: {}, timeout_ms: 30000 }),
    schema: [
      { key: 'method', label: 'Method', type: 'select', options: ['GET', 'POST', 'PUT', 'PATCH', 'DELETE'].map((m) => ({ value: m, label: m })) },
      { key: 'url', label: 'URL', type: 'text', placeholder: 'https://api.example.com/v1/…' },
      { key: 'auth_type', label: 'Authentication', type: 'select', options: [
        { value: 'none', label: 'None' }, { value: 'bearer', label: 'Bearer token' }, { value: 'basic', label: 'Basic auth' }, { value: 'header', label: 'Header' },
      ] },
      { key: 'bearer_token', label: 'Token', type: 'password', showIf: (c) => c.auth_type === 'bearer' },
      { key: 'basic_username', label: 'Username', type: 'text', showIf: (c) => c.auth_type === 'basic' },
      { key: 'basic_password', label: 'Password', type: 'password', showIf: (c) => c.auth_type === 'basic' },
      { key: 'header_name', label: 'Header name', type: 'text', showIf: (c) => c.auth_type === 'header' },
      { key: 'header_value', label: 'Header value', type: 'password', showIf: (c) => c.auth_type === 'header' },
      { key: 'headers', label: 'Headers', type: 'kv' },
      { key: 'query_params', label: 'Query parameters', type: 'kv' },
      { key: 'body_params', label: 'Body', type: 'kv', showIf: (c) => c.method && c.method !== 'GET' },
    ],
  },
  mcp: {
    label: 'MCP server', icon: Plug,
    defaults: () => ({ credential_id: null, tool_names: null }),
    schema: [
      { key: 'credential_id', label: 'Server', type: 'mcp_credential' },
      { key: 'tool_names', label: 'Tools to expose', type: 'mcp_tools' },
    ],
  },
  code: {
    label: 'Code', icon: Code2,
    defaults: () => ({
      code: `# \`args\` is what the agent passed in. Assign \`result\`.
result = {"echo": args}
`,
      requirements: [],
    }),
    schema: [
      { key: 'code', label: 'Python', type: 'code', language: 'python' },
      { key: 'requirements', label: 'pip requirements', type: 'lines' },
    ],
  },
};

export default function AgentToolsField({ value, onChange, theme, readOnly = false }) {
  const tools = Array.isArray(value) ? value : [];
  const [openIdx, setOpenIdx] = useState(null);
  const [adding, setAdding] = useState(false);

  const setTool = (i, patch) => onChange(tools.map((t, j) => (j === i ? { ...t, ...patch } : t)));
  const removeTool = (i) => { onChange(tools.filter((_, j) => j !== i)); if (openIdx === i) setOpenIdx(null); };
  const addTool = (kind) => {
    const k = TOOL_KINDS[kind];
    const name = `${k.label}${tools.filter((t) => t.kind === kind).length ? ` ${tools.filter((t) => t.kind === kind).length + 1}` : ''}`;
    onChange([...tools, { kind, name, description: '', config: k.defaults() }]);
    setOpenIdx(tools.length);
    setAdding(false);
  };

  return (
    <Field label="Tools">
      <div className="overflow-hidden rounded-md border border-[color:var(--border-default)]">
        {tools.length === 0 && !adding && (
          <div className="px-3 py-3 text-[12px] text-[color:var(--text-muted)]">No tools. The agent answers from its instructions alone.</div>
        )}
        {tools.map((t, i) => {
          const k = TOOL_KINDS[t.kind] || { label: t.kind, icon: Plug, schema: [] };
          const Icon = k.icon;
          const open = openIdx === i;
          return (
            <div key={i} className={i > 0 || adding ? 'border-t border-[color:var(--border-subtle)]' : ''}>
              <div
                role="button"
                tabIndex={0}
                aria-expanded={open}
                onClick={() => setOpenIdx(open ? null : i)}
                onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setOpenIdx(open ? null : i); } }}
                className="flex h-9 w-full cursor-pointer items-center gap-2 px-2.5 text-left transition-colors hover:bg-[color:var(--surface-hover)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-[color:var(--state-focus-ring)]"
              >
                {open ? <ChevronDown className="h-3 w-3 shrink-0 text-[color:var(--text-muted)]" /> : <ChevronRight className="h-3 w-3 shrink-0 text-[color:var(--text-muted)]" />}
                <Icon className="h-3.5 w-3.5 shrink-0 text-[color:var(--text-secondary)]" />
                <span className="flex-1 truncate text-[13px] text-[color:var(--text-primary)]">{t.name || k.label}</span>
                <span className="shrink-0 text-[11px] text-[color:var(--text-muted)]">{k.label}</span>
                {!readOnly && (
                  <button
                    type="button"
                    onClick={(e) => { e.stopPropagation(); removeTool(i); }}
                    title="Remove tool"
                    aria-label={`Remove ${t.name || k.label}`}
                    className={cx(removeBtnCls, 'h-6 w-6')}
                  >
                    <Trash2 className="h-3 w-3" />
                  </button>
                )}
              </div>
              {open && (
                <div className="space-y-3 border-t border-[color:var(--border-subtle)] bg-[color:var(--surface-sunken)] px-3 pb-3 pt-3">
                  <Field label="Name the model sees">
                    <TextInput value={t.name || ''} readOnly={readOnly} onChange={(v) => setTool(i, { name: v })} drop={false} />
                  </Field>
                  <Field label="What it is for">
                    <TextInput value={t.description || ''} readOnly={readOnly} onChange={(v) => setTool(i, { description: v })} placeholder="One line the model reads when deciding to use it" drop={false} />
                  </Field>
                  <NodeConfigFields
                    spec={{ kind: `tool_${t.kind}`, configSchema: k.schema }}
                    cfg={t.config || {}}
                    updateField={(key, v) => setTool(i, { config: { ...(t.config || {}), [key]: v } })}
                    updateFields={(patch) => setTool(i, { config: { ...(t.config || {}), ...patch } })}
                    theme={theme}
                    readOnly={readOnly}
                  />
                </div>
              )}
            </div>
          );
        })}
        {!readOnly && (adding ? (
          <div className="grid grid-cols-3 gap-1.5 border-t border-[color:var(--border-subtle)] p-1.5">
            {Object.entries(TOOL_KINDS).map(([kind, k]) => {
              const Icon = k.icon;
              return (
                <button
                  key={kind}
                  type="button"
                  onClick={() => addTool(kind)}
                  className="flex h-8 items-center gap-1.5 rounded-md border border-[color:var(--border-default)] px-2 text-[12px] text-[color:var(--text-primary)] transition-colors hover:bg-[color:var(--surface-hover)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--state-focus-ring)]"
                >
                  <Icon className="h-3.5 w-3.5 text-[color:var(--text-secondary)]" /> {k.label}
                </button>
              );
            })}
          </div>
        ) : (
          <button
            type="button"
            onClick={() => setAdding(true)}
            className={cx(ghostBtnCls, 'h-8 w-full justify-start rounded-none border-t border-[color:var(--border-subtle)] px-2.5')}
          >
            <Plus className="h-3.5 w-3.5" /> Add tool
          </button>
        ))}
      </div>
    </Field>
  );
}
